import type {IRootMachineSnapshot} from 'src/components/scheduler/booking-v2/machines/rootMachine/rootMachine.ts';
import type {IRsvpMachineSnapshot} from '../machines/rsvpMachine/rsvpMachine';
import type {IBookingMachineSnapshot} from '../machines/bookingMachine/bookingMachine';
import type {AxiosError} from 'axios';
import {getErrorMessage, getErrorMap} from '../utils/common';
import type {TranslationFunction} from 'src/types/language-support';
import {captureSentryMessage} from 'src/utils/sentry';
import type {IEvent} from '../types/IEvent';


export const selectLanguage = (state: IRootMachineSnapshot) => {
  return state.context.language;
};

export const selectRsvpStatus = (state: IRsvpMachineSnapshot) => {
  return state.context.rsvpStatus;
};

export const selectIsReschedule = (state: IRootMachineSnapshot) => {
  return state.context.currentRoute === 'reschedule';
};

export const selectError = (
  state: IRootMachineSnapshot | IBookingMachineSnapshot | IRsvpMachineSnapshot,
  t: TranslationFunction,
) => {
  const errorKey = (state.context.error as AxiosError<{errors?: string[]}>)
    ?.response?.data?.errors?.[0];

  const isError = state.context.error !== undefined;

  // TODO(Adiitya): discuss the keys
  const retryAllowedErrorKeys = [
    'PhoneNumberInvalid',
    'EmailInvalid',
    'InvalidQuestionAnswer',
    'UnavailableTimeSlot',
    'TimeSlotAlreadyBooked',
    'TimeSlotNotAvailable',
    'SameTemplateEventsFound',
  ];
  const retryAllowed = retryAllowedErrorKeys.includes(errorKey as string);
  const errorMap = getErrorMap(t);

  // If the error key is not in the error map, capture the error in Sentry
  if (errorKey && !errorMap[errorKey as keyof ReturnType<typeof getErrorMap>]) {
    captureSentryMessage(`Error: ${errorKey}`, {
      level: 'error',
      tags: {
        product: 'scheduler',
        error: state.context.error,
      },
    });
  }

  return {
    errorMessage: errorKey
      ? getErrorMessage(errorKey as keyof ReturnType<typeof getErrorMap>, t)
      : t('Something_went_wrong', 'Something went wrong'),
    allowRetry: !errorKey || retryAllowed,
    isError,
  };
};

export const selectCountryCode = (state: IRootMachineSnapshot) => {
  return state.context.countyCode;
};

export const selectRescheduleLimit = (state: IRootMachineSnapshot) => {
  const eventDetails = state.context.eventDetails as IEvent;
  return eventDetails?.reschedule_attempts_left || -1;
};

export const selectIsRescheduleFromContext = (state: IRootMachineSnapshot) => {
  return state.context.isReschedule;
};

export const selectOwner = (state: IRootMachineSnapshot) => {
  return state.context.owner;
};
